import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  createMasterSchedule, listMasterSchedules, getMasterSchedule, updateMasterSchedule,
  deleteMasterSchedule, updateScheduleEntry, updateBulkEntries, prefillSchedule, getScheduleConflicts,
  getStaffScheduleView, getStudentScheduleView, getBlockSchedules, getStaff, getStudents, getClassGroups,
  getAlternatePlans,
  getMasterScheduleOverlay,
  reconcileSignupCell,
  getCalendarEvents,
  getWeekSchoolDays,
} from "../lib/api";

import { DAYS, DAY_LABELS, DAY_COLORS, normCell, getTodayDayKey, buildDisplayNames, sortByPreferredName } from "./master-schedule/constants";
import { PrintStyles, TodayPrintStyle } from "./master-schedule/PrintStyles";
import CellEditor from "./master-schedule/CellEditor";
import AltPlanEditor from "./master-schedule/AltPlanEditor";
import MasterScheduleHeader from "./master-schedule/MasterScheduleHeader";
import TodayScheduleView from "./master-schedule/TodayScheduleView";
import StaffScheduleView from "./master-schedule/StaffScheduleView";
import StudentScheduleView from "./master-schedule/StudentScheduleView";
import MasterGrid from "./master-schedule/MasterGrid";
import TripsSection from "./master-schedule/TripsSection";
import { NewScheduleDialog, PastSchedulesDialog, ConflictsDialog, ApplyToFutureDialog, PrefillDialog } from "./master-schedule/ScheduleDialogs";
import SchedulePrintDialog from "./master-schedule/SchedulePrintDialog";
import AddAltPlanDialog from "./master-schedule/AddAltPlanDialog";
import DragDropPrompt from "./master-schedule/DragDropPrompt";
import { useAuth } from "../App";

// Return human-readable conflict messages when the SAME staff member leads/supports
// more than one class in a single block (same time). Being in other blocks is fine.
function detectBlockConflicts(classEntries, staffLookup) {
  const seen = {};
  const found = [];
  (classEntries || []).forEach((e) => {
    [e.lead_staff_id, ...(e.support_staff_ids || [])].filter(Boolean).forEach((sid) => {
      if (seen[sid]) found.push(`${staffLookup[sid] || sid} is assigned to more than one class in this block`);
      seen[sid] = true;
    });
  });
  return [...new Set(found)];
}

export default function MasterScheduleTab({ initialTodayView = false }) {
  const { isAdmin, hasPermission } = useAuth();
  const canEdit = isAdmin() || hasPermission("edit_master_schedule");
  const [schedules, setSchedules] = useState([]);
  const [activeScheduleId, setActiveScheduleId] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffLookup, setStaffLookup] = useState({});
  const [blocks, setBlocks] = useState({ full: [], half: [] });
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [showConflicts, setShowConflicts] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [templateSourceId, setTemplateSourceId] = useState(null);
  const [showPastDialog, setShowPastDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [staffViewId, setStaffViewId] = useState("");
  const [staffViewData, setStaffViewData] = useState(null);
  const [showTodayView, setShowTodayView] = useState(initialTodayView);
  const [studentList, setStudentList] = useState([]);
  const [studentLookup, setStudentLookup] = useState({});
  const [groupOptions, setGroupOptions] = useState([]);
  const [studentViewId, setStudentViewId] = useState("");
  const [studentViewData, setStudentViewData] = useState(null);
  const [pendingFutureApply, setPendingFutureApply] = useState(null);
  const [alternatePlans, setAlternatePlans] = useState([]);
  const [editingAltPlan, setEditingAltPlan] = useState(null);
  const [signupOverlay, setSignupOverlay] = useState({ by_date: {}, dates: [] });
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showAddAltPlan, setShowAddAltPlan] = useState(false);
  const [showPrefillDialog, setShowPrefillDialog] = useState(false);
  const [prefillSourceId, setPrefillSourceId] = useState("");
  // Drag-and-drop state for moving block cells / individual class cards on the grid.
  const [dragInfo, setDragInfo] = useState(null);
  const [dropPrompt, setDropPrompt] = useState(null);
  // Calendar events (Meetings & Events) overlapping the currently-viewed week.
  const [weekEvents, setWeekEvents] = useState([]);
  // Authoritative per-date school-day status for the viewed week (holidays,
  // breaks, closures, out-of-session) — mirrors the attendance roster so the
  // grid hides classes on no-school days.
  const [schoolDays, setSchoolDays] = useState({});

  const refreshSignupOverlay = async () => {
    try {
      const ovRes = await getMasterScheduleOverlay(weekMonday);
      setSignupOverlay(ovRes.data);
    } catch { /* silent */ }
  };

  const refreshCalendarEvents = async () => {
    try {
      const mon = new Date(weekMonday + "T00:00:00");
      const fri = new Date(weekMonday + "T00:00:00");
      fri.setDate(fri.getDate() + 4);
      const res = await getCalendarEvents({ start_date: fmtLocal(mon), end_date: fmtLocal(fri) });
      setWeekEvents(res.data || []);
      try {
        const sd = await getWeekSchoolDays(fmtLocal(mon), fmtLocal(fri));
        setSchoolDays(sd.data || {});
      } catch { setSchoolDays({}); }
    } catch { /* silent */ }
  };

  // Format a Date as YYYY-MM-DD from LOCAL components (avoids UTC day-shift bug)
  const fmtLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Monday of the currently-viewed week (navigable). Defaults to this week.
  const currentWeekMonday = () => {
    const d = new Date();
    const day = d.getDay();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    return fmtLocal(d);
  };
  const [weekMonday, setWeekMonday] = useState(currentWeekMonday());
  const isCurrentWeek = weekMonday === currentWeekMonday();
  const shiftWeek = (deltaWeeks) => { // eslint-disable-line no-unused-vars
    const d = new Date(weekMonday + "T00:00:00");
    d.setDate(d.getDate() + deltaWeeks * 7);
    setWeekMonday(fmtLocal(d));
  };
  // Monday of the week containing a given YYYY-MM-DD date
  const mondayOfDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const day = d.getDay();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    return fmtLocal(d);
  };
  const scheduleCovers = (s, dateStr) => !!(s && s.start_date && s.end_date && s.start_date <= dateStr && s.end_date >= dateStr);

  // Dropdown visibility: hide manually-archived schedules and (auto) those whose
  // end date is in the past, unless explicitly un-archived (archived === false).
  const isPastSchedule = (s) => !!(s.end_date && s.end_date < fmtLocal(new Date()));
  const inDropdown = (s) => (s.archived === true ? false : (s.archived === false ? true : !isPastSchedule(s)));
  const dropdownSchedules = schedules.filter((s) => inDropdown(s) || s.id === activeScheduleId);
  const pastSchedules = schedules.filter((s) => !inDropdown(s) && s.id !== activeScheduleId)
    .sort((a, b) => (b.end_date || b.start_date || "").localeCompare(a.end_date || a.start_date || ""));

  // Chronological order (by start date) for quick prev/next schedule navigation.
  const navSchedules = [...schedules].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const navIdx = navSchedules.findIndex((s) => s.id === activeScheduleId);
  const goToAdjacentSchedule = (dir) => {
    const ni = navIdx + dir;
    if (ni >= 0 && ni < navSchedules.length) setActiveScheduleId(navSchedules[ni].id);
  };

  // Map weekday key (monday/tuesday/...) → actual ISO date this week
  const dayToDate = (() => {
    const out = {};
    DAYS.forEach((dayKey, i) => {
      const d = new Date(weekMonday + "T00:00:00");
      d.setDate(d.getDate() + i);
      out[dayKey] = fmtLocal(d);
    });
    return out;
  })();

  const weekDatesArr = DAYS.map((d) => dayToDate[d]); // eslint-disable-line no-unused-vars

  // Map date -> calendar events on that date (handles multi-day spans).
  const calendarEventsByDate = (() => {
    const out = {};
    (weekEvents || []).forEach((e) => {
      DAYS.forEach((day) => {
        const ds = dayToDate[day];
        const inRange = e.end_date ? (ds >= e.date && ds <= e.end_date) : e.date === ds;
        if (inRange) { (out[ds] = out[ds] || []).push(e); }
      });
    });
    return out;
  })();
  const blockLabelFor = (block) => (block === "advisory" ? "Advisory" : block.replace("block", "Block "));
  // A date is a "half day" when any calendar event on it has the Half Day flag
  // checked in the Add Event dialog. This takes priority over copied schedule
  // blocks so the grid always shows the reduced block set on that date.
  const isHalfDayDate = (ds) => (calendarEventsByDate[ds] || []).some((e) => e.is_half_day);
  const halfDayByDay = (() => {
    const out = {};
    DAYS.forEach((day) => { out[day] = isHalfDayDate(dayToDate[day]); });
    return out;
  })();
  // A date with no school (holiday / break / closure / out-of-session) per the
  // authoritative backend calendar — the grid hides classes on these days.
  const noSchoolByDay = (() => {
    const out = {};
    DAYS.forEach((day) => {
      const info = schoolDays[dayToDate[day]];
      out[day] = info && info.is_school_day === false ? (info.no_school_reason || "No School") : null;
    });
    return out;
  })();
  const slugify = (s) => (s || "").toLowerCase().replace(/\s/g, "");

  // Sign-up options for a given day+block cell — slots whose name matches the block.
  const signupsForCell = (day, blockKey) => {
    const cell = signupOverlay.by_date?.[dayToDate[day]];
    if (!cell) return [];
    const out = [];
    Object.entries(cell.slots || {}).forEach(([slotId, sdata]) => {
      if (slugify(sdata.slot_name) === blockKey) {
        (sdata.options || []).forEach((o) => out.push({
          option: { id: o.id, title: o.title, lead_staff_name: o.lead_staff_name, location: o.location, capacity: o.capacity, slot_id: slotId },
          studentCount: o.student_count,
          studentNames: o.student_names,
        }));
      }
    });
    return out;
  };


  // Alt plans not skipped for the currently shown week
  const visibleAltPlans = alternatePlans.filter((p) => !(p.skip_dates || []).includes(weekMonday));
  // Live map of group key -> current display name, so renaming a group in
  // Settings is reflected immediately on the schedule (stored names can be stale).
  const groupLabelByKey = (() => {
    const m = { all: "All Students" };
    groupOptions.forEach((o) => { m[o.key] = o.label; });
    return m;
  })();
  const currentWeekLabel = (() => {
    try {
      const m = new Date(weekMonday + "T00:00:00");
      const f = new Date(m); f.setDate(f.getDate() + 4);
      const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(m)} – ${fmt(f)}`;
    } catch { return weekMonday; }
  })();
  const refreshAltPlans = async () => {
    try { const apRes = await getAlternatePlans({ active_only: true }); setAlternatePlans(apRes.data); } catch { /* silent */ }
  };

  const fetchBase = useCallback(async () => {
    try {
      const [schedRes, staffRes, blockRes, studRes, groupRes] = await Promise.all([
        listMasterSchedules(), getStaff({ assignable: true }), getBlockSchedules(), getStudents("current", true), getClassGroups(),
      ]);
      setSchedules(schedRes.data);
      const sl = sortByPreferredName((staffRes.data || []).filter(s => s.status !== "former"), null, "name");
      setStaffList(sl);
      const staffFirstCounts = {};
      sl.forEach(s => { const first = (s.name || "").split(" ")[0].toLowerCase(); staffFirstCounts[first] = (staffFirstCounts[first] || 0) + 1; });
      const lookup = {};
      sl.forEach(s => { const parts = (s.name || "").split(" "); const first = parts[0] || ""; const isDup = staffFirstCounts[first.toLowerCase()] > 1; lookup[s.id] = isDup ? `${first} ${(parts[parts.length - 1] || "")[0] || ""}.` : first; });
      setStaffLookup(lookup);
      setBlocks({ full: blockRes.data?.full_day?.blocks || [], half: blockRes.data?.half_day?.blocks || [] });
      const students = sortByPreferredName((studRes.data || []).filter(s => s.status === "current"), "preferred_name", "first_name");
      setStudentList(students);
      setStudentLookup(buildDisplayNames(students, "first_name", "last_name", "preferred_name"));
      const gd = groupRes.data || {};
      const opts = [];
      (gd.morning_groups || []).forEach(g => opts.push({ key: `morning_${g.key}`, label: `Morning - ${g.name}`, period: "morning" }));
      (gd.afternoon_groups || []).forEach(g => opts.push({ key: `afternoon_${g.key}`, label: `Afternoon - ${g.name}`, period: "afternoon" }));
      setGroupOptions(opts);
      // Fetch alternate plans
      try { const apRes = await getAlternatePlans({ active_only: true }); setAlternatePlans(apRes.data); } catch { /* silent */ }
      // Fetch sign-ups overlay for current week
      await refreshSignupOverlay();
      if (schedRes.data.length > 0 && !activeScheduleId) {
        const today = fmtLocal(new Date());
        const list = schedRes.data;
        // 1) schedule covering today, else 2) closest upcoming, else 3) most recent past
        let chosen = list.find(s => scheduleCovers(s, today));
        const coversToday = !!chosen;
        if (!chosen) {
          const upcoming = list.filter(s => s.start_date && s.start_date > today).sort((a, b) => a.start_date.localeCompare(b.start_date));
          chosen = upcoming[0];
        }
        if (!chosen) {
          const past = list.filter(s => s.end_date && s.end_date < today).sort((a, b) => b.end_date.localeCompare(a.end_date));
          chosen = past[0];
        }
        chosen = chosen || list[0];
        setActiveScheduleId(chosen.id);
        // If no schedule covers this week (e.g. vacation), jump the viewed week to the
        // chosen schedule's start so edits clearly land on the right schedule.
        if (!coversToday && chosen.start_date) {
          setWeekMonday(mondayOfDate(chosen.start_date));
        }
      }
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBase(); }, [fetchBase]);
  // Refresh sign-up/trip overlay whenever the viewed week changes
  useEffect(() => { refreshSignupOverlay(); refreshCalendarEvents(); /* eslint-disable-next-line */ }, [weekMonday]);
  useEffect(() => {
    if (!activeScheduleId) { setSchedule(null); return; }
    getMasterSchedule(activeScheduleId).then(res => {
      setSchedule(res.data);
      // Selecting a schedule (the only way to switch) sets the viewed week to match it:
      // current week if it covers today, otherwise the schedule's start week.
      const s = res.data;
      const today = fmtLocal(new Date());
      if (scheduleCovers(s, today)) setWeekMonday(currentWeekMonday());
      else if (s.start_date) setWeekMonday(mondayOfDate(s.start_date));
    }).catch(() => toast.error("Failed to load schedule"));
  }, [activeScheduleId]);

  // Re-fetch just the active schedule's entries (after an undo/restore) without
  // changing the currently-viewed week.
  const reloadSchedule = () => {
    if (!activeScheduleId) return;
    getMasterSchedule(activeScheduleId)
      .then(res => setSchedule(res.data))
      .catch(() => toast.error("Failed to reload schedule"));
  };

  const handleCreateSchedule = async () => {
    try {
      const res = await createMasterSchedule({ name: newName || "Master Schedule", start_date: newStartDate, end_date: newEndDate, friday_half_day: true });
      // If creating from a past schedule template, copy its classes in.
      if (templateSourceId) {
        try { await prefillSchedule(res.data.id, templateSourceId); toast.success("New schedule created from template"); }
        catch { toast.error("Schedule created, but copying classes failed"); }
      } else {
        toast.success("Schedule created");
      }
      setShowNewDialog(false); setNewName(""); setNewStartDate(""); setNewEndDate(""); setTemplateSourceId(null);
      const listRes = await listMasterSchedules(); setSchedules(listRes.data); setActiveScheduleId(res.data.id);
    } catch { toast.error("Failed to create schedule"); }
  };

  const setScheduleArchived = async (id, archived) => {
    try {
      await updateMasterSchedule(id, { archived });
      const listRes = await listMasterSchedules(); setSchedules(listRes.data);
      toast.success(archived ? "Schedule archived" : "Schedule restored");
      if (archived && id === activeScheduleId) {
        const next = listRes.data.filter(inDropdown)[0];
        setActiveScheduleId(next ? next.id : "");
      }
    } catch { toast.error("Failed to update schedule"); }
  };

  const applyAsTemplate = (sourceId) => {
    setTemplateSourceId(sourceId);
    setNewName(""); setNewStartDate(""); setNewEndDate("");
    setShowPastDialog(false);
    setShowNewDialog(true);
  };

  const handlePrefill = () => {
    if (schedules.length < 2) { toast.error("No other schedule to copy from"); return; }
    setPrefillSourceId("");
    setShowPrefillDialog(true);
  };

  const doPrefill = async () => {
    if (!prefillSourceId || prefillSourceId === activeScheduleId) return;
    const src = schedules.find(s => s.id === prefillSourceId);
    try {
      await prefillSchedule(activeScheduleId, prefillSourceId);
      const res = await getMasterSchedule(activeScheduleId); setSchedule(res.data);
      setShowPrefillDialog(false);
      toast.success(`Prefilled from "${src?.name || "selected schedule"}"`);
    }
    catch { toast.error("Failed to prefill"); }
  };

  // Reconcile auto sign-ups for a saved cell on the viewed week's date.
  // Creates sign-ups for flagged classes and removes auto sign-ups that were
  // renamed or unchecked (no stale duplicates). Called even with no flagged
  // classes so unchecking removes the previously auto-created sign-up.
  const reconcileSignupForCell = async (day, block, classEntries) => {
    const date = dayToDate[day];
    const slotName = blockLabelFor(block);
    const activities = (classEntries || [])
      .filter((c) => c.requires_signup && (c.subject || "").trim())
      .map((c) => ({
        title: (c.subject || "").trim(),
        lead_staff_id: c.lead_staff_id || null,
        support_staff_ids: c.support_staff_ids || [],
        location: c.location || "",
        capacity: (c.signup_capacity !== "" && c.signup_capacity != null && !isNaN(parseInt(c.signup_capacity, 10)))
          ? parseInt(c.signup_capacity, 10) : null,
      }));
    try {
      const res = await reconcileSignupCell({ date, slot_name: slotName, source_block: `${day}_${block}`, activities });
      await refreshSignupOverlay();
      const created = res.data?.created || 0;
      const removed = res.data?.removed || 0;
      if (created || removed) {
        const parts = [];
        if (created) parts.push(`${created} added`);
        if (removed) parts.push(`${removed} removed`);
        toast.success(`Sign-ups synced (${parts.join(", ")}) for ${slotName}, ${currentWeekLabel}`);
      }
    } catch {
      toast.error("Class saved, but failed to sync the sign-up");
    }
  };

  const handleSaveCell = async (day, block, classEntries) => {
    // A true conflict is only the SAME staff member leading/supporting more than one
    // class in THIS SAME block (same time). Being in other blocks the same day is normal.
    const uniqueConflicts = detectBlockConflicts(classEntries, staffLookup);

    if (uniqueConflicts.length > 0) {
      const proceed = window.confirm(`Scheduling Conflict Detected:\n\n${uniqueConflicts.join("\n")}\n\nSave anyway?`);
      if (!proceed) return;
    }

    try {
      await updateScheduleEntry(activeScheduleId, { day, block, entries: classEntries });
      setSchedule(prev => ({ ...prev, entries: { ...prev.entries, [`${day}_${block}`]: classEntries } }));
      await reconcileSignupForCell(day, block, classEntries);
      const currentSched = schedules.find(s => s.id === activeScheduleId);
      const hasFuture = schedules.some(s => s.id !== activeScheduleId && s.start_date && currentSched?.start_date && s.start_date > currentSched.start_date);
      if (hasFuture && isAdmin()) setPendingFutureApply({ day, block, entries: classEntries });
      else toast.success("Schedule updated");
    } catch { toast.error("Failed to save entry"); }
  };

  // ── Drag-and-drop: move/copy/swap block cells and individual class cards ──
  const cloneArr = (a) => JSON.parse(JSON.stringify(a || []));

  const handleCellDrop = (targetDay, targetBlock) => {
    if (!dragInfo) return;
    if (dragInfo.srcDay === targetDay && dragInfo.srcBlock === targetBlock) { setDragInfo(null); return; }
    const srcEntries = normCell(schedule?.entries?.[`${dragInfo.srcDay}_${dragInfo.srcBlock}`]);
    const itemLabel = dragInfo.mode === "class" ? (srcEntries[dragInfo.classIndex]?.subject || "class") : "";
    setDropPrompt({
      ...dragInfo,
      targetDay, targetBlock,
      targetLabel: `${DAY_LABELS[targetDay]} - ${blockLabelFor(targetBlock)}`,
      itemLabel,
    });
  };

  const handleDragOperation = async (operation) => {
    if (!dropPrompt) return;
    const { srcDay, srcBlock, targetDay, targetBlock, mode, classIndex } = dropPrompt;
    const srcKey = `${srcDay}_${srcBlock}`;
    const tgtKey = `${targetDay}_${targetBlock}`;
    const srcEntries = normCell(schedule?.entries?.[srcKey]);
    const tgtEntries = normCell(schedule?.entries?.[tgtKey]);
    let newSrc = null; // null → source cell unchanged (Copy)
    let newTgt;
    if (mode === "cell") {
      const dragged = cloneArr(srcEntries);
      if (operation === "copy") newTgt = [...cloneArr(tgtEntries), ...dragged];
      else if (operation === "swap") { newTgt = cloneArr(srcEntries); newSrc = cloneArr(tgtEntries); }
      else { newTgt = dragged; newSrc = []; } // replace (move)
    } else {
      const card = cloneArr([srcEntries[classIndex]]);
      const srcWithout = cloneArr(srcEntries.filter((_, i) => i !== classIndex));
      if (operation === "copy") newTgt = [...cloneArr(tgtEntries), ...card];
      else if (operation === "swap") { newTgt = card; newSrc = [...srcWithout, ...cloneArr(tgtEntries)]; } // positional swap: dragged class ↔ target cell contents
      else { newTgt = card; newSrc = srcWithout; } // replace (move)
    }
    const uniqueConflicts = detectBlockConflicts(newTgt, staffLookup);
    if (uniqueConflicts.length > 0) {
      const proceed = window.confirm(`Scheduling Conflict Detected:\n\n${uniqueConflicts.join("\n")}\n\nSave anyway?`);
      if (!proceed) { setDropPrompt(null); setDragInfo(null); return; }
    }
    const payload = { [tgtKey]: newTgt };
    if (newSrc !== null) payload[srcKey] = newSrc;
    try {
      await updateBulkEntries(activeScheduleId, { entries: payload });
      setSchedule((prev) => ({ ...prev, entries: { ...prev.entries, ...payload } }));
      await reconcileSignupForCell(targetDay, targetBlock, newTgt);
      if (newSrc !== null) await reconcileSignupForCell(srcDay, srcBlock, newSrc);
      toast.success("Schedule updated");
    } catch { toast.error("Failed to move classes"); }
    setDropPrompt(null); setDragInfo(null);
  };

  const handleApplyToFuture = async (apply) => {
    if (apply && pendingFutureApply) {
      try { const res = await updateScheduleEntry(activeScheduleId, { ...pendingFutureApply, apply_to_future: true }); const count = res.data?.future_schedules_updated || 0; toast.success(`Updated this schedule + ${count} future schedule${count !== 1 ? "s" : ""}`); }
      catch { toast.error("Failed to apply to future schedules"); }
    } else toast.success("Schedule updated");
    setPendingFutureApply(null);
  };

  const handleCheckConflicts = async () => {
    try { const res = await getScheduleConflicts(activeScheduleId); setConflicts(res.data); setShowConflicts(true); }
    catch { toast.error("Failed to check conflicts"); }
  };

  const handleStaffView = async (staffId) => {
    if (!staffId || staffId === "all") { setStaffViewData(null); setStaffViewId(staffId || ""); return; }
    setStudentViewId(""); setStudentViewData(null);
    try { const res = await getStaffScheduleView(activeScheduleId, staffId); setStaffViewData(res.data); setStaffViewId(staffId); }
    catch { toast.error("Failed to load staff view"); }
  };

  const handleStudentView = async (studentId) => {
    if (!studentId || studentId === "all") { setStudentViewData(null); setStudentViewId(studentId || ""); return; }
    setStaffViewId(""); setStaffViewData(null);
    try { const res = await getStudentScheduleView(activeScheduleId, studentId); setStudentViewData(res.data); setStudentViewId(studentId); }
    catch { toast.error("Failed to load student view"); }
  };

  const handleDeleteSchedule = async () => {
    if (!window.confirm("Delete this schedule permanently?")) return;
    try { await deleteMasterSchedule(activeScheduleId); toast.success("Schedule deleted"); const listRes = await listMasterSchedules(); setSchedules(listRes.data); setActiveScheduleId(listRes.data[0]?.id || ""); }
    catch { toast.error("Failed to delete"); }
  };

  const handlePrint = () => setShowPrintDialog(true);
  // Whole-week print: ensure the master grid (not a single staff/student view) is
  // showing, then trigger the browser print (CSS isolates the .print-area grid).
  const printWholeWeek = () => {
    setShowTodayView(false);
    setStaffViewId("all"); setStaffViewData(null);
    setStudentViewId("all"); setStudentViewData(null);
    setTimeout(() => window.print(), 350);
  };
  const getBlocksForDay = (day) => {
    const ds = dayToDate[day];
    if (noSchoolByDay[day]) return [];
    if (isHalfDayDate(ds)) return blocks.half;
    if (day === "friday" && schedule?.friday_half_day) return blocks.half;
    return blocks.full;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]"></div></div>;

  const todayKey = getTodayDayKey();
  const todayLabel = todayKey ? DAY_LABELS[todayKey] : null;
  const todayBlocks = todayKey ? getBlocksForDay(todayKey) : [];

  return (
    <div className="space-y-4" data-testid="master-schedule-tab">
      <PrintStyles />
      <TodayPrintStyle active={showTodayView} />

      {/* Header Controls */}
      <MasterScheduleHeader
        activeScheduleId={activeScheduleId} setActiveScheduleId={setActiveScheduleId} dropdownSchedules={dropdownSchedules}
        canEdit={canEdit} setTemplateSourceId={setTemplateSourceId} setShowNewDialog={setShowNewDialog} setShowPastDialog={setShowPastDialog} pastSchedules={pastSchedules}
        schedule={schedule} setSchedule={setSchedule} handlePrefill={handlePrefill} handleCheckConflicts={handleCheckConflicts} handlePrint={handlePrint}
        handleDeleteSchedule={handleDeleteSchedule} setScheduleArchived={setScheduleArchived} reloadSchedule={reloadSchedule}
        todayKey={todayKey} showTodayView={showTodayView} setShowTodayView={setShowTodayView} setWeekMonday={setWeekMonday} currentWeekMonday={currentWeekMonday}
        setStaffViewId={setStaffViewId} setStaffViewData={setStaffViewData} setStudentViewId={setStudentViewId} setStudentViewData={setStudentViewData}
        staffViewId={staffViewId} handleStaffView={handleStaffView} staffList={staffList} staffLookup={staffLookup}
        studentViewId={studentViewId} handleStudentView={handleStudentView} studentList={studentList} studentLookup={studentLookup}
        setShowAddAltPlan={setShowAddAltPlan}
      />

      {/* No schedules exist yet — prompt to create one */}
      {schedules.length === 0 && (
        <Card className="no-print border-2 border-dashed"><CardContent className="py-12 text-center space-y-3" data-testid="no-schedules-empty">
          <p className="text-lg font-medium text-muted-foreground">No master schedules exist yet</p>
          <p className="text-sm text-muted-foreground">Create your first schedule and set the date range it applies to.</p>
          {canEdit && <Button onClick={() => setShowNewDialog(true)} className="bg-[#00305E]" data-testid="empty-new-schedule-btn"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M12 5v14"/><path d="M5 12h14"/></svg>Create Schedule</Button>}
        </CardContent></Card>
      )}

      {/* Active schedule date-range banner — always shows which schedule you're editing */}
      {schedule && !showTodayView && (() => {
        const wkFri = dayToDate.friday;
        const coversWeek = !!(schedule.start_date && schedule.end_date && schedule.start_date <= wkFri && schedule.end_date >= weekMonday);
        return (
          <div className={`no-print rounded-lg px-4 py-2 text-sm flex items-center justify-between gap-3 flex-wrap ${coversWeek ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-amber-50 border border-amber-200 text-amber-900"}`} data-testid="schedule-coverage-banner">
            <div>
              <span className="font-semibold">Editing:</span> {schedule.name}
              {schedule.start_date && schedule.end_date
                ? <span className="ml-1">({schedule.start_date} → {schedule.end_date})</span>
                : <span className="ml-1 italic">(no date range set)</span>}
              <span className="inline-flex items-center gap-0.5 ml-2 align-middle">
                <button
                  type="button"
                  onClick={() => goToAdjacentSchedule(-1)}
                  disabled={navIdx <= 0}
                  title="Previous schedule"
                  data-testid="prev-schedule-btn"
                  className="p-0.5 rounded hover:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                </button>
                <button
                  type="button"
                  onClick={() => goToAdjacentSchedule(1)}
                  disabled={navIdx < 0 || navIdx >= navSchedules.length - 1}
                  title="Next schedule"
                  data-testid="next-schedule-btn"
                  className="p-0.5 rounded hover:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </button>
              </span>
              {!coversWeek && <span className="block text-xs mt-0.5">⚠ This schedule does not cover the week you're viewing ({currentWeekLabel}). Any edits apply to this schedule's whole date range — not just this week.</span>}
            </div>
            {!coversWeek && schedule.start_date && (
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => setWeekMonday(mondayOfDate(schedule.start_date))} data-testid="jump-to-schedule-week-btn">Jump to this schedule's week</Button>
            )}
          </div>
        );
      })()}

      {/* Trips summary — pinned at the top of the main schedule view (all staff) */}
      {schedule && (!staffViewId || staffViewId === "all") && (!studentViewId || studentViewId === "all") && (
        <TripsSection
          mode={showTodayView ? "today" : "week"}
          signupOverlay={signupOverlay}
          dayToDate={dayToDate}
          todayKey={todayKey}
          canEdit={canEdit}
          staffList={staffList}
          studentList={studentList}
          weekDates={weekDatesArr}
          onChanged={refreshSignupOverlay}
        />
      )}

      {/* Today's Schedule */}
      {showTodayView && schedule && todayKey && (
        <TodayScheduleView
          schedule={schedule} todayKey={todayKey} todayLabel={todayLabel} todayBlocks={todayBlocks}
          visibleAltPlans={visibleAltPlans} blocks={blocks} canEdit={canEdit} setEditingAltPlan={setEditingAltPlan}
          signupsForCell={signupsForCell} signupOverlay={signupOverlay} dayToDate={dayToDate}
          staffLookup={staffLookup} studentList={studentList} refreshSignupOverlay={refreshSignupOverlay} setEditingCell={setEditingCell}
          groupLabelByKey={groupLabelByKey} noSchoolReason={noSchoolByDay[todayKey]}
        />
      )}

      {showTodayView && !todayKey && <Card className="no-print"><CardContent className="py-12 text-center"><p className="text-lg text-muted-foreground">It's the weekend — no school schedule today.</p><Button variant="outline" className="mt-4" onClick={() => setShowTodayView(false)}>View Full Week</Button></CardContent></Card>}

      {/* Staff View */}
      {staffViewData && staffViewId && staffViewId !== "all" && !showTodayView && (
        <StaffScheduleView staffViewData={staffViewData} staffViewId={staffViewId} staffLookup={staffLookup} schedule={schedule} blocks={blocks} getBlocksForDay={getBlocksForDay} />
      )}

      {/* Student View */}
      {studentViewData && studentViewId && studentViewId !== "all" && !showTodayView && (
        <StudentScheduleView studentViewData={studentViewData} schedule={schedule} blocks={blocks} getBlocksForDay={getBlocksForDay} />
      )}

      {/* Master Grid */}
      {schedule && (!staffViewId || staffViewId === "all") && (!studentViewId || studentViewId === "all") && !showTodayView && (
        <MasterGrid
          schedule={schedule} blocks={blocks} getBlocksForDay={getBlocksForDay} visibleAltPlans={visibleAltPlans}
          canEdit={canEdit} setEditingAltPlan={setEditingAltPlan} signupsForCell={signupsForCell} signupOverlay={signupOverlay}
          dayToDate={dayToDate} staffLookup={staffLookup} studentLookup={studentLookup} studentList={studentList}
          refreshSignupOverlay={refreshSignupOverlay} setEditingCell={setEditingCell} currentWeekLabel={currentWeekLabel} isCurrentWeek={isCurrentWeek}
          groupLabelByKey={groupLabelByKey} calendarEventsByDate={calendarEventsByDate} halfDayByDay={halfDayByDay} noSchoolByDay={noSchoolByDay}
          onCellDragStart={(day, block) => setDragInfo({ srcDay: day, srcBlock: block, mode: "cell" })}
          onClassDragStart={(day, block, classIndex) => setDragInfo({ srcDay: day, srcBlock: block, mode: "class", classIndex })}
          onCellDrop={handleCellDrop}
          onItemDragEnd={() => setDragInfo(null)}
          dragActive={!!dragInfo}
        />
      )}

      {!schedule && !loading && <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground mb-4">No master schedule yet. Create one to get started.</p><Button onClick={() => setShowNewDialog(true)} className="bg-[#00305E] hover:bg-[#00305E]/90">Create First Schedule</Button></CardContent></Card>}

      {/* Cell Editor */}
      {editingCell && <CellEditor entries={normCell(schedule?.entries?.[`${editingCell.day}_${editingCell.block}`])} staffList={staffList} staffLookup={staffLookup} studentList={studentList} studentLookup={studentLookup} groupOptions={groupOptions} dayLabel={DAY_LABELS[editingCell.day]} blockLabel={editingCell.block === "advisory" ? "Advisory" : editingCell.block.replace("block", "Block ")} accentColor={DAY_COLORS[editingCell.day]?.accent} onSave={(classEntries) => handleSaveCell(editingCell.day, editingCell.block, classEntries)} onClose={() => setEditingCell(null)} />}

      <NewScheduleDialog
        showNewDialog={showNewDialog} setShowNewDialog={setShowNewDialog} templateSourceId={templateSourceId} setTemplateSourceId={setTemplateSourceId} schedules={schedules}
        newName={newName} setNewName={setNewName} newStartDate={newStartDate} setNewStartDate={setNewStartDate} newEndDate={newEndDate} setNewEndDate={setNewEndDate} handleCreateSchedule={handleCreateSchedule}
      />

      <PastSchedulesDialog showPastDialog={showPastDialog} setShowPastDialog={setShowPastDialog} pastSchedules={pastSchedules} canEdit={canEdit} setActiveScheduleId={setActiveScheduleId} setScheduleArchived={setScheduleArchived} applyAsTemplate={applyAsTemplate} />

      <ConflictsDialog showConflicts={showConflicts} setShowConflicts={setShowConflicts} conflicts={conflicts} />

      <ApplyToFutureDialog pendingFutureApply={pendingFutureApply} handleApplyToFuture={handleApplyToFuture} />

      <SchedulePrintDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        activeScheduleId={activeScheduleId}
        schedule={schedule}
        blocks={blocks}
        staffList={staffList}
        studentList={studentList}
        staffLookup={staffLookup}
        studentLookup={studentLookup}
        onPrintWholeWeek={printWholeWeek}
      />

      {/* Alt Plan Editor (edit / skip-week / remove) */}
      <AltPlanEditor
        plan={editingAltPlan}
        open={!!editingAltPlan}
        onClose={() => setEditingAltPlan(null)}
        blocks={blocks.full || []}
        weekMonday={weekMonday}
        weekLabel={currentWeekLabel}
        onChanged={refreshAltPlans}
      />

      {/* Add Alternate Plan shortcut (with student picker) */}
      <AddAltPlanDialog
        open={showAddAltPlan}
        onOpenChange={setShowAddAltPlan}
        studentList={studentList}
        studentLookup={studentLookup}
        blocks={blocks.full || []}
        onChanged={refreshAltPlans}
      />

      {/* Prefill from another schedule */}
      <PrefillDialog
        open={showPrefillDialog}
        onOpenChange={setShowPrefillDialog}
        sources={[...schedules].filter(s => s.id !== activeScheduleId).sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""))}
        selectedId={prefillSourceId}
        setSelectedId={setPrefillSourceId}
        onConfirm={doPrefill}
        todayStr={fmtLocal(new Date())}
      />

      {/* Drag-and-drop operation prompt (Copy / Swap / Replace Only) */}
      <DragDropPrompt
        prompt={dropPrompt}
        onChoose={handleDragOperation}
        onCancel={() => { setDropPrompt(null); setDragInfo(null); }}
      />
    </div>
  );
}
